import Panel from './panel';
import { getLogger } from '../../logging/logger';

const logger = getLogger('PerformanceStats');
const bytesPerMB = 1048576;

/**
 * @typedef {Object} PanelConfig
 * @property {string} [fg] - Foreground color
 * @property {string} [bg] - Background color
 * @property {number} [PR] - Pixel ratio
 * @property {number} [width] - Width
 * @property {number} [height] - Height
 */

/**
 * @typedef {Object} PerformanceMemory
 * @property {number} usedJSHeapSize - Used JS heap size
 * @property {number} jsHeapSizeLimit - JS heap size limit
 */

/**
 * @typedef {Performance & {memory?: PerformanceMemory}} ExtendedPerformance
 */

/**
 * Performance statistics display utility
 */
export default class PerformanceStats {
	/**
	 * @param {PanelConfig} [fpsConfig] - FPS panel configuration
	 * @param {PanelConfig} [msConfig] - Milliseconds panel configuration
	 * @param {PanelConfig} [memConfig] - Memory panel configuration
	 */
	constructor(fpsConfig, msConfig, memConfig) {
		this.container = document.createElement('div');
		this.container.classList.add('performance-stats');

		this.container.addEventListener('click', (e) => this.hidePanel(e), false);

		this.beginTime = (performance || Date).now();
		this.prevTime = this.beginTime;
		this.frames = 0;

		// Store config for resize
		this.fpsConfig = fpsConfig;
		this.msConfig = msConfig;
		this.memConfig = memConfig;

		this.initializePanels();
		this.setupResizeHandler();

		this.dom = this.container;
	}

	/**
	 * Initialize performance monitoring panels
	 */
	initializePanels() {
		const panelWidthRatio = 0.1;
		const width = document.documentElement.clientWidth * panelWidthRatio;
		const height = width * 0.4;
		const PR = width / 80;

		this.fpsPanel = this.addPanel(
			new Panel('fps', { fg: '#0ff', bg: '#002', PR, width, height, ...this.fpsConfig }),
		);
		this.msPanel = this.addPanel(
			new Panel('ms', { fg: '#0f0', bg: '#020', PR, width, height, ...this.msConfig }),
		);
		if (/** @type {ExtendedPerformance} */ (performance)?.memory)
			this.memPanel = this.addPanel(
				new Panel('mb', { fg: '#f08', bg: '#201', PR, width, height, ...this.memConfig }),
			);
	}

	/**
	 * Setup window resize event handler
	 */
	setupResizeHandler() {
		this.resizeHandler = () => this.handleResize();
		window.addEventListener('resize', this.resizeHandler);
	}

	/**
	 * Handle window resize event
	 */
	handleResize() {
		const panelWidthRatio = 0.1;
		const width = document.documentElement.clientWidth * panelWidthRatio;
		const height = width * 0.4;
		const PR = width / 80;

		[this.fpsPanel, this.msPanel, this.memPanel].forEach((panel) => {
			if (panel) {
				panel.resize(width, height, PR);
			}
		});
	}

	/**
	 * Add a panel to the container
	 * @param {Panel} panel - The panel to add
	 * @returns {Panel} The added panel
	 */
	addPanel(panel) {
		logger.log('[addPanel]', { panel });
		if (panel.dom) {
			this.container.appendChild(panel.dom);
		}
		return panel;
	}

	/**
	 * Toggle panel visibility
	 * @param {MouseEvent} event - The click event
	 */
	hidePanel(event) {
		logger.log('[hidePanel]', { event });

		event.preventDefault();

		const panelElement = /** @type {HTMLElement} */ (event.target);
		if (panelElement?.classList.contains('hide')) {
			panelElement.classList.remove('hide');
		} else if (panelElement?.classList) {
			panelElement.classList.add('hide');
		}
	}

	/**
	 * Begin performance measurement
	 */
	begin() {
		logger.log('[begin]');
		this.beginTime = (performance || Date).now();
	}

	/**
	 * End performance measurement and update panels
	 * @returns {number} Current timestamp
	 */
	end() {
		logger.log('[end]');
		this.frames += 1;

		const time = (performance || Date).now();

		this.msPanel?.update(time - this.beginTime, 30);

		if (time >= this.prevTime + 1000) {
			this.fpsPanel?.update((this.frames * 1000) / (time - this.prevTime), 75);

			this.prevTime = time;
			this.frames = 0;

			if (this.memPanel) {
				const { memory } = /** @type {ExtendedPerformance} */ (performance);
				if (memory) {
					this.memPanel?.update(
						memory.usedJSHeapSize / bytesPerMB,
						(memory.jsHeapSizeLimit / bytesPerMB) * 0.6,
					);
				}
			}
		}
		return time;
	}

	/**
	 * Update performance statistics
	 */
	update() {
		logger.debug('[update]');
		this.beginTime = this.end();
	}

	/**
	 * Clean up event listeners and resources
	 */
	destroy() {
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
			this.resizeHandler = null;
		}
	}
}
